import React, { useState } from "react"
import { Box, IconButton, Tooltip, Typography, useTheme } from "@mui/material"
import { Edit, Group } from "@mui/icons-material"
import { FeatureFlag } from "../../../../services/feature-flag.service"
import { AdminToggle, DataTable, DataTableColumn, EmptyState, StatusBadge, TagChip } from "../../../../components/ui"

type Props = {
    flags: FeatureFlag[]
    onToggleEnabled: (flag: FeatureFlag) => void
    onEdit: (flag: FeatureFlag) => void
    onManageOverrides: (flag: FeatureFlag) => void
    countLabelText?: string
}

export const FeatureFlagTable: React.FC<Props> = ({
    flags,
    onToggleEnabled,
    onEdit,
    onManageOverrides,
    countLabelText,
}) => {
    const theme = useTheme()
    const [page, setPage] = useState(1)
    const PAGE_SIZE = 25

    const columns: DataTableColumn<FeatureFlag>[] = [
        {
            key: "name",
            header: "Flag",
            width: "2fr",
            render: (row) => (
                <Box
                    component="span"
                    title={row.name}
                    sx={{
                        display: "inline-block",
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        px: 1,
                        py: 0.25,
                        borderRadius: "6px",
                        fontFamily: "monospace",
                        fontSize: "13px",
                        fontWeight: 500,
                        backgroundColor: theme.palette.brand.cream,
                        border: `1px solid ${theme.palette.divider}`,
                        color: "text.primary",
                    }}
                >
                    {row.name}
                </Box>
            ),
        },
        {
            key: "status",
            header: "Status",
            width: "120px",
            render: (row) => <StatusBadge status={row.enabled ? "Ativo" : "Inativo"} />,
        },
        {
            key: "rollout",
            header: "Rollout",
            width: "100px",
            render: (row) => (
                <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
                    {row.rollout_percentage}%
                </Typography>
            ),
        },
        {
            key: "description",
            header: "Descrição",
            width: "2.5fr",
            render: (row) => (
                <Typography
                    sx={{
                        fontSize: "13px",
                        color: "text.secondary",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {row.description || "—"}
                </Typography>
            ),
        },
        {
            key: "overrides",
            header: "Overrides",
            width: "90px",
            render: (row) => (
                <TagChip label={String(row.overrides_count ?? 0)} maxChars={5} />
            ),
        },
        {
            key: "actions",
            header: "Ações",
            width: "140px",
            align: "right",
            render: (row) => (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                    <Tooltip title="Editar flag">
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onEdit(row) }}
                            sx={{ borderRadius: "9px" }}
                        >
                            <Edit sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Gerenciar overrides por usuário">
                        <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); onManageOverrides(row) }}
                            sx={{ borderRadius: "9px" }}
                        >
                            <Group sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <AdminToggle
                        checked={row.enabled}
                        onChange={() => onToggleEnabled(row)}
                    />
                </Box>
            ),
        },
    ]

    return (
        <DataTable<FeatureFlag>
            columns={columns}
            rows={flags.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)}
            rowKey={(row) => String(row.id)}
            page={page}
            pageSize={PAGE_SIZE}
            totalRows={flags.length}
            onPageChange={setPage}
            countLabel={countLabelText}
            emptyState={
                <EmptyState
                    title="Nenhuma feature flag cadastrada"
                    description="Crie a primeira flag para começar."
                />
            }
            minWidth={700}
        />
    )
}
